import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium">{label}</Label>
      {children}
    </div>
  );
}

export default function Machines() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", code: "" });
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/machines")).data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/machines", form);
      setForm({ name: "", code: "" });
      toast.success("Machine added");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/machines/${confirmId}`);
      toast.success("Machine deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); setConfirmId(null); }
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mb-1">Master</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Machines</h1>
        <p className="text-slate-500 text-sm mt-2">Machines appear in the production log dropdown when filling a job card.</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-slate-200 lg:col-span-2">
          <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>All Machines</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Code</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-slate-500">No machines yet</td></tr>}
              {rows.map((m) => (
                <tr key={m.id} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-medium">{m.name}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{m.code || "—"}</td>
                  <td className="py-2 text-right"><Button variant="ghost" size="icon" onClick={() => setConfirmId(m.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add Machine</div>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Offset Press 1" required /></Field>
            <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. OFF-01" /></Field>
            <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800"><Plus className="w-4 h-4 mr-2" /> Add</Button>
          </form>
        </Card>
      </div>
      <ConfirmDialog
        open={!!confirmId}
        title="Delete Machine?"
        description="This will permanently delete the machine. This cannot be undone."
        confirmLabel="Delete Machine"
        busy={deleting}
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
    </Layout>
  );
}
